"use client";

import React, { useState, useEffect } from "react";
import { Wrench, Brain, Code, Bug, Presentation, Info, Cpu, Sparkles } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface PrincipleItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  desc: {
    vi: string;
    en: string;
  };
}
const PRINCIPLES: PrincipleItem[] = [{
  id: "hands-on",
  icon: Wrench,
  title: "HANDS-ON",
  desc: {
    vi: "Trực tiếp lắp ráp và thao tác với thiết bị",
    en: "Direct assembly and hands-on hardware operation"
  }
}, {
  id: "thinking",
  icon: Brain,
  title: "THINKING",
  desc: {
    vi: "Phát triển tư duy logic, trình tự và giải quyết vấn đề",
    en: "Developing logical thinking, sequencing, and problem-solving"
  }
}, {
  id: "programming",
  icon: Code,
  title: "PROGRAMMING",
  desc: {
    vi: "Từng bước chuyển từ tư duy trực quan sang lập trình robot",
    en: "Step-by-step transition from visual thinking to robot programming"
  }
}, {
  id: "test-debug",
  icon: Bug,
  title: "TEST & DEBUG",
  desc: {
    vi: "Thử nghiệm, phát hiện lỗi và cải tiến",
    en: "Testing, bug detection, and continuous improvement"
  }
}, {
  id: "presentation",
  icon: Presentation,
  title: "PRESENTATION",
  desc: {
    vi: "Trình bày và giải thích sản phẩm / giải pháp",
    en: "Presenting and explaining products and solutions"
  }
}];
export default function LearningByDoing() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  useEffect(() => {
    const readLang = () => {
      if (typeof window === "undefined") return;
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    };
    readLang();
    const handleLangChange = () => {
      readLang();
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  return <section id="learningbydoing" data-nav="light" ref={ref} className="relative bg-background text-foreground py-16 lg:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header with Tech Spec Rail Accent */}
        <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-12">
          {/* Spec rail accent with technical notch */}
          <div className="flex items-center justify-center gap-2 mb-4" aria-hidden="true">
            <div className="h-[1px] w-12 bg-secondary" />
            <div className="size-2 bg-secondary rotate-45" />
            <div className="h-[1px] w-12 bg-secondary" />
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground uppercase">
            {lang === "vi" ? "HỌC BẰNG CÁCH LÀM" : "LEARNING BY DOING"}
          </h2>
        </div>

        {/* Featured Hands-on Robotics Assembly Photograph Showcase */}
        <div className={`mb-12 lg:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
            {/* Tech Spec Highlight Rail */}
            <div className="h-1 bg-gradient-to-r from-primary via-secondary to-accent w-full" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
              {/* Image Container */}
              <div className="lg:col-span-8 relative aspect-[16/9] lg:aspect-[21/10] overflow-hidden bg-slate-950">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_hands_on_robotics_assembly.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_hands_on_robotics_assembly.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_hands_on_robotics_assembly.png-wvc-sizes" alt="ROBOTO academy students hands-on assembling advanced robotics hardware" loading="lazy" className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-slate-950/40 lg:to-slate-950" />
                
                {/* Tech Badge Overlay */}
                <div className="absolute top-4 left-4 bg-slate-900/90 backdrop-blur-md border border-secondary/40 text-secondary text-xs font-mono font-bold px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-sm">
                  <Cpu className="size-4 animate-pulse" />
                  <span>HARDWARE LAB // LIVE BUILD</span>
                </div>
              </div>

              {/* Technical Context Panel */}
              <div className="lg:col-span-4 p-6 lg:p-8 bg-slate-900 text-white flex flex-col justify-between h-full">
                <div>
                  <div className="flex items-center gap-2 text-xs font-mono text-secondary uppercase tracking-widest mb-3">
                    <Sparkles className="size-3.5 text-accent" />
                    <span>ROBOTO ACADEMY LAB</span>
                  </div>
                  <h3 className="text-xl lg:text-2xl font-serif font-bold text-white mb-3 leading-snug">
                    {lang === "vi" ? "Trải Nghệ Kỹ Thuật Thực Tế" : "Hands-On Engineering Practice"}
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">
                    {lang === "vi" ? "Học viên trực tiếp làm việc với vi điều khiển, động cơ servo và hệ thống cảm biến thực tế tại phòng Lab hiện đại." : "Students work directly with microcontrollers, servo motors, and sensor systems in our technical academy lab environment."}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400 font-mono">
                  <span>SPEC // HW-ENG-416</span>
                  <span className="text-secondary font-bold">100% HARDWARE</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5-Column Principle Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
          {PRINCIPLES.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.id} data-index={i} className={`bg-card text-card-foreground border border-border shadow-2xs hover:shadow-md hover:border-secondary transition-all duration-300 rounded-xl flex flex-col justify-between group h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${i * 60}ms`,
            transitionProperty: "opacity, transform, border-color, box-shadow"
          }}>
                <CardHeader className="pt-6 pb-2">
                  <div className="size-12 rounded-xl bg-muted flex items-center justify-center text-foreground group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300 mb-4">
                    <Icon className="size-6" />
                  </div>
                  <CardTitle className="text-base font-bold font-sans tracking-wide text-foreground">
                    <h3>{item.title}</h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-6 pt-0">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc[lang]}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Cross-Cutting Principles Footnote */}
        <div className={`mt-10 lg:mt-14 max-w-3xl mx-auto rounded-lg bg-muted/60 border border-border/80 p-4 flex items-start gap-3 transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "350ms"
      }}>
          <Info className="size-5 text-secondary shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {lang === "vi" ? <>
                <strong className="text-foreground font-medium">Ghi chú:</strong> Đây là các nguyên tắc xuyên suốt trong phương pháp đào tạo, không phải tuyên bố rằng cả 5 bước đều xuất hiện trong từng buổi học riêng lẻ.
              </> : <>
                <strong className="text-foreground font-medium">Note:</strong> These represent cross-cutting principles integrated across our comprehensive training methodology, rather than sequential steps strictly required in every single session.
              </>}
          </p>
        </div>
      </div>
    </section>;
}